import { useRef, useState, useCallback, useEffect } from "react";
import { useLangStore } from "../../store/langStore";
import { t as tr } from "../../lib/i18n";
import type { NbSignal } from "../../lib/jsonContracts";
import { StatusChip } from "../../design-system/primitives/StatusChip";
import { GENERIC_NODES, brandFromModelKey } from "../../lib/genericNodes";
import { suggestDataset } from "../../lib/sclMapper";
import type { GenericNodeType } from "../../lib/genericNodes";

interface Props {
  rows:          NbSignal[];
  selectedIed:   string;
  sclNodes:      unknown[];
  modelKey:      string;
  selectedIdx:   Set<number>;
  onToggleSelect:(idx: number, ctrl: boolean) => void;
  onRowsChange:  (rows: NbSignal[]) => void;
  onInferSignal: (signal: string, insertAt: number) => Promise<void>;
  onEditSignal:  (idx: number, nuevoNombre: string) => Promise<void>;
  onEditNode:    (idx: number, nuevoNodo: string) => void;
  onConvertGeneric: (idx: number, typeId: string) => void;
  onPickAlternative: (idx: number, label: string, score: number) => void;
  onSetDsTarget: (idx: number, value: string) => void;
  dsOptions: string[];
  mapEnabled: boolean;
}

const ROW_H = 38;

// Menú "⋮" por fila: convierte el nodo nativo a un genérico según la marca del IED.
function RowMenu({ modelKey, onPick }: { modelKey: string; onPick: (typeId: string) => void }) {
  const { lang } = useLangStore();
  const [open, setOpen] = useState(false);
  const brand = brandFromModelKey(modelKey);
  const tipos: GenericNodeType[] = brand ? GENERIC_NODES[brand] : [];
  return (
    <div className="relative shrink-0">
      <button data-tutorial="generic-dots"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--color-surface-bright)] text-[var(--color-on-surface-variant)] hover:text-[var(--color-brand)] text-xs leading-none"
        title={tr("Convertir a nodo genérico", lang)}>⋮</button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute right-0 top-full z-50 mt-0.5 rounded border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-lg py-1 min-w-[260px]">
            {tipos.length === 0 ? (
              <div className="px-3 py-1.5 text-[11px] text-[var(--color-on-surface-variant)] italic">
                {tr("Selecciona un IED para ver opciones", lang)}
              </div>
            ) : tipos.map(t => (
              <button key={t.id}
                onClick={(e) => { e.stopPropagation(); onPick(t.id); setOpen(false); }}
                className="block w-full text-left px-3 py-1.5 text-[11px] text-[var(--color-on-surface)] hover:bg-[var(--color-surface-variant)]">
                {tr(t.label, lang)}
                <span className="block font-mono text-[9px] text-[var(--color-on-surface-variant)]">{t.gen(1)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Celda editable por doble clic (nombre de señal o nodo MMS).
// ── Triage de confianza: semáforo + desplegable top-3 ──
// Umbrales: verde ≥90% · ámbar 65–89.9% · rojo <65%
function confColor(score: number): string {
  if (score >= 0.90) return "var(--color-status-present)";
  if (score >= 0.65) return "#e6a817";
  return "var(--color-status-absent)";
}
function ConfidenceBadge({ score, topK, onPick }:
  { score?: number; topK?: { label: string; score: number }[]; onPick: (label: string, score: number) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  if (score === undefined) return <span className="w-14 shrink-0" />;
  const color = confColor(score);
  const lang  = useLangStore.getState().lang;
  return (
    <div ref={ref} className="relative w-14 shrink-0">
      <button
        data-tutorial="conf-badge"
        onClick={(e) => { e.stopPropagation(); if (topK && topK.length > 1) setOpen(o => !o); }}
        className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-[var(--color-surface-bright)] transition-colors"
        title={tr("Confianza", lang) + `: ${(score * 100).toFixed(1)}%` +
               (topK && topK.length > 1 ? tr(" — clic para ver alternativas", lang) : "")}>
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
        <span className="font-mono text-[10px]" style={{ color }}>{Math.round(score * 100)}%</span>
      </button>
      {open && topK && (
        <div className="absolute right-0 top-6 z-30 min-w-72 rounded border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-lg">
          <div className="px-2 pb-1 text-[10px] uppercase tracking-wide text-[var(--color-on-surface-variant)] opacity-60">
            {tr("Alternativas", lang)}
          </div>
          {topK.map(t => (
            <button key={t.label}
              onClick={(e) => { e.stopPropagation(); onPick(t.label, t.score); setOpen(false); }}
              className="flex w-full items-center justify-between gap-3 px-2 py-1 text-left hover:bg-[var(--color-brand-soft-10)]">
              <span className="truncate font-mono text-[11px] text-[var(--color-on-surface)]">{t.label}</span>
              <span className="shrink-0 font-mono text-[10px]" style={{ color: confColor(t.score) }}>
                {(t.score * 100).toFixed(1)}%
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableCell({
  value, className, title, style, onCommit, onClickWhenIdle,
}: {
  value: string; className: string; title?: string; style?: React.CSSProperties;
  onCommit: (val: string) => void; onClickWhenIdle?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) { setDraft(value); setTimeout(() => ref.current?.select(), 0); } }, [editing, value]);
  if (editing) {
    const commit = () => { setEditing(false); const v = draft.trim(); if (v && v !== value) onCommit(v); };
    return (
      <input ref={ref} value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setEditing(false); } }}
        onBlur={commit}
        onClick={e => e.stopPropagation()}
        style={style}
        className={className + " bg-[var(--color-surface)] border border-[var(--color-brand)] rounded outline-none px-1"} />
    );
  }
  return (
    <span style={style} className={className} title={title} onDoubleClick={() => setEditing(true)} onClick={onClickWhenIdle}>{value}</span>
  );
}

// Fila de edición inline — soporta pegado masivo (varias líneas → varias filas).
function EditingRow({
  selectedIed, onConfirm, onCancel,
  signalWidth,
}: { selectedIed: string; onConfirm: (val: string) => void; onCancel: () => void; signalWidth?: number }) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const multilinea = /\r?\n/.test(draft.trim());
  const nLineas = draft.split(/\r?\n/).map(s => s.trim()).filter(Boolean).length;

  return (
    <div className="flex items-start gap-2 px-3 border-b-2 border-[var(--color-brand)] bg-[var(--color-brand-soft-10)]"
      style={{ minHeight: ROW_H, paddingTop: 6, paddingBottom: 6 }}>
      <textarea
        ref={inputRef}
        value={draft}
        rows={multilinea ? Math.min(nLineas, 8) : 1}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" && !e.shiftKey && draft.trim()) { e.preventDefault(); onConfirm(draft); }
          if (e.key === "Escape") { onCancel(); }
        }}
        placeholder={tr("Nombre de la señal → Enter para inferir…", useLangStore.getState().lang)}
        style={signalWidth ? { width: signalWidth } : undefined}
        className="w-96 shrink-0 resize-none bg-transparent border-none outline-none font-mono text-[11px] text-[var(--color-brand)] placeholder:text-[var(--color-on-surface-variant)] placeholder:text-[10px] leading-tight"
      />
      <span className="text-[10px] text-[var(--color-on-surface-variant)] italic flex-1 truncate pt-0.5">
        {multilinea
          ? `${nLineas} señales — Enter para añadir todas`
          : (selectedIed ? `IED: ${selectedIed}` : tr("← Selecciona IED en panel derecho", useLangStore.getState().lang))}
      </span>
      <button onClick={onCancel}
        className="shrink-0 text-[var(--color-on-surface-variant)] hover:text-[var(--color-status-absent)] text-xs px-1 pt-0.5">✕</button>
    </div>
  );
}

export function SignalsTable({ rows, selectedIed, sclNodes, modelKey, selectedIdx, onToggleSelect, onRowsChange, onInferSignal, onEditSignal, onEditNode, onConvertGeneric, onPickAlternative, onSetDsTarget, dsOptions, mapEnabled }: Props) {
  const hasSCL = sclNodes.length > 0;
  const [colW, setColW] = useState<Record<string, number>>(() => loadColW());

  // Posición de inserción activa: null = sin inserción, número = insertar antes de ese índice
  // -1 = insertar al final
  const [insertPos, setInsertPos] = useState<number | null>(null);
  const [hoveredGap, setHoveredGap] = useState<number | null>(null);
  const [isInferring, setIsInferring] = useState(false);

  const handleConfirm = useCallback(async (signal: string) => {
    if (insertPos === null) return;
    setIsInferring(true);
    const pos = insertPos === -1 ? rows.length : insertPos;
    await onInferSignal(signal, pos);
    setInsertPos(null);
    setIsInferring(false);
  }, [insertPos, rows.length, onInferSignal]);

  const handleCancel = useCallback(() => setInsertPos(null), []);

  const deleteRow = useCallback((idx: number) => {
    onRowsChange(rows.filter((_, i) => i !== idx));
  }, [rows, onRowsChange]);

  // Construir lista visual intercalando la fila de edición
  // Cada item es { type: 'row', row, idx } | { type: 'editing' }
  type Item =
    | { type: 'row'; row: NbSignal; idx: number }
    | { type: 'editing' };

  const items: Item[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (insertPos === i) items.push({ type: 'editing' });
    items.push({ type: 'row', row: rows[i], idx: i });
  }
  // Al final
  if (insertPos === -1 || insertPos === rows.length) {
    items.push({ type: 'editing' });
  }

  // Vista vacía
  if (rows.length === 0 && insertPos === null) {
    return (
      <div className="flex h-full flex-col">
        <TableHeader hasSCL={hasSCL} mapEnabled={mapEnabled} colW={colW} setColW={setColW} />
        <div className="flex flex-1 items-center justify-center flex-col gap-3">
          <p className="text-sm text-[var(--color-on-surface-variant)]">
            {selectedIed ? `IED: ${selectedIed}` : tr("Sin señales. Añade la primera.", useLangStore.getState().lang)}
          </p>
          <button data-tutorial="add-signal" onClick={() => setInsertPos(0)}
            className="flex items-center gap-2 rounded border border-[var(--color-brand-border-30)] bg-[var(--color-brand-soft-10)] px-4 py-2 text-sm text-[var(--color-brand)] hover:bg-[var(--color-brand-soft-20)]">
            {tr("+ Añadir señal", useLangStore.getState().lang)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <datalist id="ds-target-opts">
        {dsOptions.map(d => <option key={d} value={d} />)}
      </datalist>
      <TableHeader hasSCL={hasSCL} mapEnabled={mapEnabled} colW={colW} setColW={setColW} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {items.map((item, vi) => {
          if (item.type === 'editing') {
            return (
              <EditingRow key={`edit-${vi}`}
                signalWidth={colW.signal}
                selectedIed={selectedIed}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            );
          }
          const { row: r, idx } = item;
          const iedColor = r.status === "present" ? "var(--color-status-present)"
                         : r.status === "absent"  ? "var(--color-status-absent)"
                         : "var(--color-on-surface-variant)";
          const isLast = idx === rows.length - 1;
          return (
            <div key={`row-${idx}`} className="relative group">
              {/* Gap superior — mostrar + para insertar ANTES de esta fila */}
              {insertPos === null && (
                <div
                  onMouseEnter={() => setHoveredGap(idx)}
                  onMouseLeave={() => setHoveredGap(null)}
                  className="absolute top-0 left-0 right-0 flex items-center justify-center"
                  style={{ height: 8, marginTop: -4, zIndex: 10 }}>
                  {hoveredGap === idx && (
                    <button
                      onClick={() => setInsertPos(idx)}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-[var(--color-brand)] text-black text-xs shadow hover:scale-110 transition-transform leading-none"
                      title={tr("Insertar señal aquí", useLangStore.getState().lang)}>+</button>
                  )}
                </div>
              )}

              {/* Fila de datos */}
              <div className={"flex items-center gap-2 px-3 border-b border-[var(--color-outline-variant)] " +
                  (selectedIdx.has(idx) ? "bg-[var(--color-brand-soft-15)]" : "hover:bg-[var(--color-surface-variant)]")}
                style={{ height: ROW_H }}
                onClick={(e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); onToggleSelect(idx, true); } }}>
                <EditableCell
                  value={r.signal}
                  style={{ width: colW.signal }}
                  className="shrink-0 truncate font-mono text-[11px] text-[var(--color-brand)] cursor-text"
                  title={r.signal + tr(" — doble clic para editar", useLangStore.getState().lang)}
                  onCommit={(v) => { void onEditSignal(idx, v); }}
                />
                <EditableCell
                  value={r.mmsNode}
                  className="flex-1 truncate font-mono text-[11px] text-[var(--color-on-surface-variant)] cursor-pointer"
                  title={r.mmsNode + tr(" — clic para copiar", useLangStore.getState().lang)}
                  onCommit={(v) => onEditNode(idx, v)}
                  onClickWhenIdle={() => navigator.clipboard?.writeText(r.mmsNode)}
                />
                <ConfidenceBadge score={r.score} topK={r.topK}
                  onPick={(label, score) => onPickAlternative(idx, label, score)} />
                <span style={{ width: colW.ied }} className="shrink-0 truncate text-[11px]">{r.iedRef}</span>
                {hasSCL && <span className="shrink-0 truncate font-mono text-[10px]" style={{ color: iedColor, width: colW.iedName }}>{r.iedName ?? "—"}</span>}
                {hasSCL && <span style={{ width: colW.dsScl }} className="shrink-0 truncate text-[10px] text-[var(--color-on-surface-variant)]">{r.dataset ?? "—"}</span>}
                {hasSCL && r.status && <span style={{ width: colW.estado }} className="shrink-0"><StatusChip status={r.status === "present" ? "present" : "absent"} /></span>}
                {mapEnabled && (
                  <input
                    list="ds-target-opts"
                    value={r.dsTarget ?? ""}
                    placeholder={r.mmsNode && r.mmsNode !== "N/A" ? suggestDataset(r.mmsNode) : ""}
                    onChange={(e) => onSetDsTarget(idx, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: colW.dsTarget }}
                    className="shrink-0 rounded border border-transparent bg-transparent px-1 py-0.5 font-mono text-[10px] text-[var(--color-on-surface)] placeholder:text-[var(--color-on-surface-variant)]/50 placeholder:italic hover:border-[var(--color-outline-variant)] focus:border-[var(--color-brand)] focus:bg-[var(--color-surface)] outline-none"
                  />
                )}
                <div className="ml-auto shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <RowMenu modelKey={modelKey} onPick={(typeId) => onConvertGeneric(idx, typeId)} />
                  <button onClick={(e) => { e.stopPropagation(); deleteRow(idx); }}
                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--color-surface-bright)] text-[var(--color-on-surface-variant)] hover:text-[var(--color-status-absent)] text-xs"
                    title={tr("Eliminar señal", useLangStore.getState().lang)}>✕</button>
                </div>
              </div>

              {/* Gap inferior de la ÚLTIMA fila — permite añadir al final (C3) */}
              {isLast && insertPos === null && (
                <div
                  onMouseEnter={() => setHoveredGap(rows.length)}
                  onMouseLeave={() => setHoveredGap(null)}
                  className="flex items-center justify-center border-b border-dashed border-[var(--color-outline-variant)]"
                  style={{ height: 24 }}>
                  {hoveredGap === rows.length && (
                    <button
                      onClick={() => setInsertPos(-1)}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-[var(--color-brand)] text-black text-xs shadow hover:scale-110 transition-transform leading-none"
                      title={tr("Añadir señal al final", useLangStore.getState().lang)}>+</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {isInferring && (
          <div className="px-3 py-2 text-[11px] text-[var(--color-brand)] animate-pulse">
            {tr("Infiriendo nodo MMS…", useLangStore.getState().lang)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Columnas redimensionables: anchos en px, persistidos en localStorage ──
export const DEFAULT_COLW: Record<string, number> = {
  signal: 384, ied: 128, iedName: 160, dsScl: 176, estado: 96, dsTarget: 96,
};
const COLW_KEY = "axon.tags.colw";
export function loadColW(): Record<string, number> {
  try {
    const raw = localStorage.getItem(COLW_KEY);
    if (raw) return { ...DEFAULT_COLW, ...JSON.parse(raw) as Record<string, number> };
  } catch { /* defaults */ }
  return { ...DEFAULT_COLW };
}
function saveColW(w: Record<string, number>) {
  try { localStorage.setItem(COLW_KEY, JSON.stringify(w)); } catch { /* ignorar */ }
}

/** Manija de arrastre en el límite derecho de una columna del encabezado. */
function Resizer({ col, colW, setColW }:
  { col: string; colW: Record<string, number>; setColW: (w: Record<string, number>) => void }) {
  return (
    <span
      onMouseDown={(e) => {
        e.preventDefault(); e.stopPropagation();
        const x0 = e.clientX, w0 = colW[col];
        const move = (ev: MouseEvent) => {
          const w = Math.max(56, w0 + (ev.clientX - x0));
          const next = { ...colW, [col]: w };
          setColW(next);
        };
        const up = () => {
          document.removeEventListener("mousemove", move);
          document.removeEventListener("mouseup", up);
          document.body.style.cursor = "";
          saveColW({ ...colW });
        };
        document.body.style.cursor = "col-resize";
        document.addEventListener("mousemove", move);
        document.addEventListener("mouseup", up);
      }}
      onDoubleClick={() => { const next = { ...colW, [col]: DEFAULT_COLW[col] }; setColW(next); saveColW(next); }}
      title="Arrastra para ajustar el ancho · doble clic restaura"
      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-[var(--color-brand)]/40 active:bg-[var(--color-brand)]/60"
    />
  );
}

function TableHeader({ hasSCL, mapEnabled, colW, setColW }:
  { hasSCL: boolean; mapEnabled: boolean; colW: Record<string, number>; setColW: (w: Record<string, number>) => void }) {
  const { lang } = useLangStore();
  const H = ({ col, children, title }: { col: string; children: React.ReactNode; title?: string }) => (
    <span className="relative shrink-0 truncate border-r border-[var(--color-outline-variant)] pr-2"
          style={{ width: colW[col] }} title={title}>
      {children}
      <Resizer col={col} colW={colW} setColW={setColW} />
    </span>
  );
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface-container-low)] px-3 py-2 pr-8 text-[11px] font-medium uppercase tracking-wide text-[var(--color-on-surface-variant)]">
      <H col="signal">{tr("Nombre señal",lang)}</H>
      <span className="flex-1 truncate border-r border-[var(--color-outline-variant)] pr-2">{tr("Nodo MMS",lang)}</span>
      <H col="ied">{tr("Ref. IED",lang)}</H>
      {hasSCL && <H col="iedName">IEDname SCL</H>}
      {hasSCL && <H col="dsScl">Dataset SCL</H>}
      {hasSCL && <H col="estado">{tr("Estado",lang)}</H>}
      {mapEnabled && <H col="dsTarget" title={tr("DataSet donde se guardará el nodo al mapear",lang)}>{tr("DataSet →",lang)}</H>}
    </div>
  );
}
